import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { requireService, wrap } from '@repo/di-ez';
import { Scrollbar } from '@repo/go-ui';
import { hasValue } from '@repo/utils';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SortableSettingItem } from '../../components/SortableSettingItem';
import { TRAJECTORY_2D_CHART_CONFIGS, type Trajectory2dChartTypeId } from '../trajectory-graph/trajectory-2d-charts';

import styles from './trajectory-graph-settings.module.scss';

export const TrajectoryGraphSettings = wrap(function TrajectoryGraphSettings() {
  const { t } = useTranslation();
  const graphStore = requireService('trajectoryGraphStore');
  const uiStore = requireService('trajectoryUiStore');

  const orderedConfigs = graphStore.chart2dOrder
    .map((id) => TRAJECTORY_2D_CHART_CONFIGS.find((config) => config.id === id))
    .filter(hasValue);

  const handleVisibilityChange = (id: string, visible: boolean) => {
    graphStore.setChart2dVisibility(id as Trajectory2dChartTypeId, visible);
    uiStore.emitStateChange();
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeId = event.active.id;
      const overId = event.over?.id;

      if (!hasValue(overId) || !hasValue(activeId) || activeId === overId) {
        return;
      }

      const order = [...graphStore.chart2dOrder];
      const oldIndex = order.findIndex((id) => id === activeId);
      const newIndex = order.findIndex((id) => id === overId);

      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      graphStore.setChart2dOrder(arrayMove(order, oldIndex, newIndex));
      uiStore.emitStateChange();
    },
    [graphStore, uiStore],
  );

  return (
    <Scrollbar className={styles.scroll}>
      <DndContext modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedConfigs.map((config) => config.id)}>
          <div className={styles.list}>
            {orderedConfigs.map((config) => (
              <SortableSettingItem
                key={config.id}
                id={config.id}
                title={t(config.labelKey)}
                isVisible={graphStore.chart2dVisibility[config.id] !== false}
                isVisibilitySwitcherDisabled={graphStore.isChart2dVisibilityDisabled(config.id)}
                onVisibilityChange={handleVisibilityChange}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </Scrollbar>
  );
});
